import { arrayLiteralToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { z } from 'zod';

import { AuditResponseSchema } from './auditSchema.ts';
import { StatusTypesEnum } from './commonSchema.ts';
import {
  createPaginatedResponseSchema,
  paginatedResponseSchema,
} from './pagingSchema.ts';

export const TirePositionEnum = z.enum(
  ['None', 'FrontLeft', 'FrontRight', 'BackLeft', 'BackRight'],
  {
    error: 'required',
  }
);

export const TirePositionCamelEnum = z.enum(
  arrayLiteralToCamelCase(TirePositionEnum.options)
);

export const TreadDepthMeasurementEnum = z.enum(['inch', 'mm']);

export const VehicleGeneralAttributesSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
});

export const GetVehicleAttributesAttributeSchema = z.object({
  key: z.string().nullish(),
  label: z.string().nullish(),
  value: z.string().nullish(),
});

export type GetVehicleAttributesAttributeModel = z.infer<
  typeof GetVehicleAttributesAttributeSchema
>;

export const GetVehicleAttributesSchema = VehicleGeneralAttributesSchema.extend(
  {
    dateAdded: z.string(),
  }
);

export const FulfilmentToIdSchema = z.object({
  fulfilmentToId: z.uuid(),
});

export const VehicleOrderSpecificAttributesSchema = z.object({
  orderId: z.uuid(),
  dateAdded: z.coerce.date<Date>(),
  key: z.string().nullish(),
  label: z.string().nullish(),
  value: z.string().nullish(),
});

export const VehicleSchema = z.object({
  id: z.uuid(),
  vrn: z.string().optional(),
  vin: z.string().optional(),
  make: z.string().optional(),
  makeId: z.string().optional(),
  model: z.string().optional(),
  modelId: z.string().optional(),
  variant: z.string().optional(), // Engine for Epicor and HaynesPro
  variantId: z.string().optional(), // Engine for Epicor and HaynesPro
  type: z.string().optional(), // Type for HaynesPro
  typeId: z.string().optional(), // Type for HaynesPro
  mileage: z.number().nullish(),
  style: z.string().optional(),
  year: z.number().optional(),
  fuel_type: z.string().optional(),
  length: z.string().optional(),
  height: z.string().optional(),
  width: z.string().optional(),
  weight: z.string().optional(),
  lastServiceDate: z.string().optional(),
  motExpiryDate: z.string().optional(),
  recommendedOil: z.string().optional(),
  engine: z.string().optional(),
  engineId: z.string().optional(),
  license: z.string().optional(),
  state: z.string().optional(),
  color: z.string().optional(),
  generalAttributes: VehicleGeneralAttributesSchema.array().optional(),
  orderSpecificAttributes:
    VehicleOrderSpecificAttributesSchema.array().optional(),
  customerId: z.uuid().optional(),
  driverId: z.uuid().optional(),
  accountNumber: z.string().optional(),
  serviceLocationId: z.string().optional(),
  isRetained: z.boolean().optional(),
  fulfilmentId: z.uuid().optional(),
});

export const VehicleFieldsEnum = VehicleSchema.omit({
  id: true,
  generalAttributes: true,
}).keyof();

export const VehicleEditSchema = VehicleSchema.pick({
  id: true,
  color: true,
  mileage: true,
  vin: true,
  license: true,
});

export const UpdateFulfilmentToRequestSchema = z.object({
  dataType: z.string().nullish(),
  reference: z.string().nullish(),
  referenceSub: z.string().nullish(),
  referenceStatic: z.string().nullish(),
  description: z.string().nullish(),
});

export const UpdateFulfilmentToResponseSchema =
  UpdateFulfilmentToRequestSchema.extend({
    fulfilmentToId: z.uuid(),
    customerId: z.uuid(),
  });

export const VehicleListSchema = z.array(VehicleSchema);

export const CreateVehicleSchema = VehicleSchema.omit({
  id: true,
  generalAttributes: true,
}).extend(
  z.object({
    customerId: z.uuid(),
    serviceLocationId: z.string().optional(),
    isRetained: z.boolean().optional(),
    accountNumber: z.string().optional(),
  }).shape
);

export const CreateVehicleFormSchema = z.object({
  vin: z.string().optional(),
  vrn: z.string().optional(),
  make: z.string(),
  model: z.string(),
  variant: z.string(),
  type: z.string(),
  mileage: z.number().nullish(),
  year: z.number(),
  license: z.string().optional(),
  state: z.string().optional(),
  color: z.string().optional(),
  isRetained: z.boolean().optional(),
});

const VehicleObject = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});

export const CreateEpicorVehicleFormSchema = CreateVehicleFormSchema.omit({
  make: true,
  model: true,
  variant: true,
  type: true,
}).extend({
  make: VehicleObject,
  model: VehicleObject,
  variant: VehicleObject,
  type: VehicleObject,
});

export const CreateHaynesProVehicleFormSchema = CreateVehicleFormSchema.omit({
  make: true,
  model: true,
  variant: true,
  type: true,
}).extend({
  make: VehicleObject,
  model: VehicleObject,
  variant: VehicleObject,
  type: VehicleObject,
});

const ManualVehicleObject = z.object({
  label: z.string().min(1),
  value: z.string().optional(),
});

export const CreateManualVehicleFormSchema = CreateVehicleFormSchema.omit({
  make: true,
  model: true,
  variant: true,
  type: true,
}).extend({
  make: ManualVehicleObject,
  model: ManualVehicleObject,
  variant: ManualVehicleObject,
  type: ManualVehicleObject,
});

export const LicensePlateSearchRequestSchema = z.object({
  license: z.string().min(2),
  country: z.string().optional(),
  state: z.string(),
});

export const LicenseToVINResponseSchema = z.array(z.string()).or(
  z.object({
    code: z.number(),
    timestamp: z.number(),
    errorMessage: z.string(),
  })
);

export const VehicleLookupByVINResponseSchema = z.object({
  operationId: z.string(),
  vehicle: z.object({
    year: z.string(),
    makeTypeId: z.string(),
    make: z.string(),
    makeText: z.string(),
    model: z.string(),
    modelText: z.string(),
    engine: z.string(),
    engineText: z.string(),
    vin: z.string(),
  }),
  vinInfoFields: z
    .array(
      z.object({
        code: z.string(),
        header: z.string(),
        info: z.string(),
      })
    )
    .optional()
    .nullish(),
  specificConditions: z
    .array(
      z.object({
        id: z.string(),
        question: z.string(),
        type: z.string(),
        multiSelect: z.boolean(),
        choices: z
          .array(
            z.object({
              id: z.string(),
              text: z.string(),
              selected: z.boolean(),
            })
          )
          .optional()
          .nullish(),
      })
    )
    .optional()
    .nullish(),
});

export const HaynesProVehicleType = z.object({
  code: z.number(),
  text: z.string(),
});

export const HaynesProLicensePlateSearchResponseSchema = z.object({
  make: z.string(),
  model: z.string(),
  variant: z.string(),
  variantId: z.string(),
  type: z.array(HaynesProVehicleType),
  year: z.string(),
});

export const HaynesProLicensePlateSearchFESchema =
  HaynesProLicensePlateSearchResponseSchema.omit({
    type: true,
  }).extend({
    type: z.array(VehicleObject),
  });

export const LicensePlateSearchResponseSchema = VehicleSchema.pick({
  year: true,
  make: true,
  makeId: true,
  model: true,
  modelId: true,
  variant: true,
  variantId: true,
})
  .required()
  .extend({ vin: z.string().optional() });

export const FleetVehiclesSchema = VehicleSchema.pick({
  id: true,
  make: true,
  makeId: true,
  model: true,
  modelId: true,
  vrn: true,
  variant: true,
  variantId: true,
  mileage: true,
}).extend(
  z.object({
    vin: z.string().optional(),
    fleetId: z.string().optional(),
    status: StatusTypesEnum,
    isDeleted: z.boolean(),
    engineCode: z.string().optional(),
    axleCode: z.string().optional(),
    dateOfLastService: z.coerce.date<Date>().nullish().optional(),
    customerId: z.string().optional(),
  }).shape
);

/// CRUD
export const FleetVehicleCreateEditStep1 = z.object({
  fleetAccountId: z.string().nonempty(),
  axleCode: z.string().optional(),
  colour: z.string().optional(),
  description: z.string().optional(),
  engineCode: z.string().optional(),
  engineSize: z.string().optional(),
  engineId: z.string().optional(),
  fuelType: z.string().optional(),
  state: z.string().optional(),
  make: z.string(),
  makeId: z.string().optional(),
  year: z.number().optional(),
  model: z.string(),
  modelId: z.string().optional(),
  type: z.string().optional(),
  typeId: z.string().optional(),
  vin: z.string().optional(),
  vrn: z.string().optional(),
  license: z.string().optional(),
  isRetained: z.boolean().optional(),
  variant: z.string(),
  variantId: z.string().optional(),
  color: z.string().optional(),
  accountNumber: z.string().optional(),
  engine: z.string().optional(),
});
export const FleetVehicleCreateEditStep2 = z.object({
  mileage: z.number(),
  customerId: z.string().optional(),
  accountNumber: z.string().optional(),
  driverId: z.uuid().optional(),
  serviceLocationId: z.string().optional(),
  dateOfLastService: z.coerce.date<Date>().nullish().optional(),
  createMode: z.boolean().optional(),
  vehicleId: z.string().optional(),
});

export const FleetVehicleCreateSchema = FleetVehicleCreateEditStep1.extend(
  FleetVehicleCreateEditStep2.shape
);

export const FleetVehicleCreateResponseSchema = z.object({
  fleetAccountId: z.string().nullish(),
  vehicleId: z.string().nullish(),
  fulfilmentToId: z.string().nullish(),
});

export const UpdateFleetVehicleRequestSchema = FleetVehicleCreateSchema.extend({
  vehicleId: z.uuid(),
  fulfilmentId: z.uuid().optional(),
  driverId: z.uuid().optional(),
  generalAttributes: VehicleGeneralAttributesSchema.array().optional(),
});

export const FleetVehicleValidateAutoIntegrateRequestSchema = z.object({
  vin: z.string().min(1),
  accountNumber: z.string(),
  serviceLocation: z.uuid(),
});

export const FleetVehicleValidateAutoIntegrateResponseSchema = z.object({
  isSuccess: z.boolean(),
  validationErrors: z.string().optional(),
  vin: z.string(),
});

// END CRUD

// Order Schemas

export const UpdateVehicleOrderRequestSchema = z.object({
  orderId: z.uuid(),
  vehicle: VehicleSchema,
});

export const FulfilmentToAttributesSchema = z.object({
  key: z.string(),
  value: z.string(),
  label: z.string().optional(),
  dateAdded: z.string().optional(),
});

export const FulfilmentToOrderSpecificAttributesSchema = z.object({
  orderId: z.uuid(),
  isLatest: z.boolean().optional(),
  data: FulfilmentToAttributesSchema.omit({ dateAdded: true })
    .extend({ dateAdded: z.string() })
    .array()
    .nullish(),
});

export const UpdateFulfilmentToOrderSpecificAttributesSchema = z.object({
  orderId: z.uuid(),
  fulfilmentToId: z.uuid(),
  attributes: FulfilmentToAttributesSchema.array(),
});

export const UpdateFulfilmentToOrderSpecificAttributesResponseSchema =
  UpdateFulfilmentToOrderSpecificAttributesSchema.omit({ attributes: true });

const VehicleFulfilledPartsPartsSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  number: z.string().nullish(),
  quantity: z.string(),
});

const VehicleFulfilledItemsJobSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  parts: VehicleFulfilledPartsPartsSchema.array(),
});

export const VehicleFulfilledPartsJobBeSchema = z.object({
  id: z.uuid(),
  status: z.string(),
  items: VehicleFulfilledItemsJobSchema.array(),
});

export const VehicleFulfilledPartsJobFeSchema =
  VehicleFulfilledPartsJobBeSchema.extend({
    description: z.string(),
    quantity: z.number(),
    customerName: z.string(),
  });

export const VehicleFulfilledPartsBeSchema = z.object({
  id: z.uuid(),
  orderNumber: z.string(),
  customer: z
    .object({
      id: z.uuid(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
    })
    .optional(),
  jobs: VehicleFulfilledPartsJobBeSchema.array(),
});

export const VehicleFulfilledPartsFeSchema = VehicleFulfilledPartsBeSchema.omit(
  { jobs: true }
).extend({
  jobs: VehicleFulfilledPartsJobFeSchema.array(),
});

export const VehicleFulfilledPartsResponseSchema = z.object({
  orders: VehicleFulfilledPartsBeSchema.array(),
});

// Customer Schemas
export const CustomerVehicleSchema = z.object({
  customerId: z.uuid(),
  isDeleted: z.boolean(),
  orderStatus: z.string(),
  vehicle: VehicleSchema,
});

export const FulfilmentToOrderIdSchema = FulfilmentToIdSchema.extend({
  orderId: z.uuid(),
});

export const DeleteVehicleRequestSchema = z.object({
  fulfilmentToId: z.uuid(),
  customerIds: z.uuid().array(),
});

export const FleetDeleteVehicleRequestSchema = z.object({
  fleetId: z.uuid(),
  id: z.uuid(),
});

export const SearchFulfilmentToSchema = z.object({
  vin: z.string(),
});

const OrderSpecificAttributeSchema = z.object({
  orderId: z.uuid(),
  data: z
    .array(
      FulfilmentToAttributesSchema.omit({ dateAdded: true }).extend({
        dateAdded: z.string(),
      })
    )
    .nullish(),
  isLatest: z.boolean(),
});

export const FulfilmentToSchema = z.object({
  id: z.uuid(),
  customerId: z.uuid(),
  reference: z.string().nullish(),
  referenceSub: z.string().nullish(),
  referenceStatic: z.string().nullish(),
  dataType: z.string().nullish(),
  dataTypeVersion: z.int(),
  description: z.string().nullish(),
  generalAttributes: z.array(VehicleGeneralAttributesSchema).nullish(),
  orderSpecificAttributes: z.array(OrderSpecificAttributeSchema).nullish(),
  isDeleted: z.boolean(),
  audit: AuditResponseSchema,
});

export const SearchFulfilmentToResponseSchema =
  createPaginatedResponseSchema(FulfilmentToSchema);

export const UpdateFulfilmentToFeSchema = z.object({
  fulfilmentToId: z.uuid(),
  fulfilmentTo: UpdateFulfilmentToRequestSchema.extend({
    customerId: z.uuid(),
  }),
});

export const CustomerVehicleListSchema = z.array(CustomerVehicleSchema);

export const VehicleMileageFormSchema = z.object({
  mileage: z.number().min(0),
});

export const TechnicianVehicleHistoryPartsSchema = z.object({
  name: z.string(),
  quantity: z.number(),
  status: z.string().nullish(),
});

export const TechnicianVehicleHistorySchema = z.object({
  orderId: z.string(),
  orderReference: z.string(),
  vehicleMileage: z.number(),
  customerName: z.string().nullish(),
  contactName: z.string().nullish(),
  reference: z.string().nullish(),
  description: z.string().nullish(),
  technicianIds: z.uuid().array(),
  orderDateCreated: z.coerce.date<Date>().nullish(),
  dateUpdated: z.coerce.date<Date>().nullish(),
  parts: TechnicianVehicleHistoryPartsSchema.array(),
  status: z.string().nullish(),
  orderCompletedOn: z.coerce.date().nullish(),
  orderStatus: z.string().nullish(),
});

export const GetTechnicianVehicleHistorySchema = z.object({
  vehicleReference: z.string(),
  jobStatus: z.string().optional(),
  pageNumber: z.number().nonnegative().optional(),
  resultsPerPage: z.number().nonnegative().optional(),
});

export const GetTechnicianVehicleHistoryResponseSchema =
  paginatedResponseSchema({
    results: TechnicianVehicleHistorySchema,
  });

export const AddFulfilmentToRequestSchema = z.object({
  customerId: z.uuid(),
  dataType: z.string().nullish(),
  reference: z.string().nullish(),
  referenceSub: z.string().nullish(),
  referenceStatic: z.string().nullish(),
  description: z.string().nullish(),
  generalAttributes: z.array(VehicleGeneralAttributesSchema).nullish(),
  orderSpecificAttributes: z
    .array(FulfilmentToOrderSpecificAttributesSchema)
    .nullish(),
});

export const GetFulfilmentToResponseSchema =
  AddFulfilmentToRequestSchema.extend({
    dateCreated: z.string(),
  });

export const UpdateFulfilmentToGeneralAttributeRequestSchema = z.object({
  attributes: VehicleGeneralAttributesSchema.array(),
});

export type VehicleModel = z.infer<typeof VehicleSchema>;
export type VehicleObjectType = z.infer<typeof VehicleObject>;
export type VehicleEditModel = z.infer<typeof VehicleEditSchema>;
export type VehicleListModel = z.infer<typeof VehicleListSchema>;
export type VehicleFieldsType = z.infer<typeof VehicleFieldsEnum>;
export type VehicleGeneralAttributesModel = z.infer<
  typeof VehicleGeneralAttributesSchema
>;
export type VehicleOrderSpecificAttributesModel = z.infer<
  typeof VehicleOrderSpecificAttributesSchema
>;
export type GetVehicleAttributesModel = z.infer<
  typeof GetVehicleAttributesSchema
>;
export type CreateVehicleModel = z.infer<typeof CreateVehicleSchema>;
export type CreateVehicleFormModel = z.infer<typeof CreateVehicleFormSchema>;
export type UpdateFulfilmentToFeModel = z.infer<
  typeof UpdateFulfilmentToFeSchema
>;
export type CreateEpicorVehicleFormModel = z.infer<
  typeof CreateEpicorVehicleFormSchema
>;

export type CreateHaynesProVehicleFormModel = z.infer<
  typeof CreateHaynesProVehicleFormSchema
>;

export type LicensePlateSearchRequestModel = z.infer<
  typeof LicensePlateSearchRequestSchema
>;

export type LicenseToVINResponseModel = z.infer<
  typeof LicenseToVINResponseSchema
>;

export type VehicleLookupByVINResponseModel = z.infer<
  typeof VehicleLookupByVINResponseSchema
>;

export type HaynesProVehicleTypeModel = z.infer<typeof HaynesProVehicleType>;

export type HaynesProLicensePlateSearchResponseModel = z.infer<
  typeof HaynesProLicensePlateSearchResponseSchema
>;

export type HaynesProLicensePlateSearchFEModel = z.infer<
  typeof HaynesProLicensePlateSearchFESchema
>;

export type LicensePlateSearchResponseModel = z.infer<
  typeof LicensePlateSearchResponseSchema
>;

export type CreateManualVehicleFormModel = z.infer<
  typeof CreateManualVehicleFormSchema
>;

export type FulfilmentToIdModel = z.infer<typeof FulfilmentToIdSchema>;
// Order Models
export type UpdateVehicleOrderRequestModel = z.infer<
  typeof UpdateVehicleOrderRequestSchema
>;
export type FulfilmentToAttributesModel = z.infer<
  typeof FulfilmentToAttributesSchema
>;

export type GetFulfilmentToOrderSpecificAttributesModel = z.infer<
  typeof FulfilmentToOrderSpecificAttributesSchema
>;
export type UpdateFulfilmentToOrderSpecificAttributesModel = z.infer<
  typeof UpdateFulfilmentToOrderSpecificAttributesSchema
>;
export type UpdateFulfilmentToOrderSpecificAttributesResponseModel = z.infer<
  typeof UpdateFulfilmentToOrderSpecificAttributesResponseSchema
>;
export type VehicleFulfilledPartsJobFeModel = z.infer<
  typeof VehicleFulfilledPartsJobFeSchema
>;
export type VehicleFulfilledPartsFeModel = z.infer<
  typeof VehicleFulfilledPartsFeSchema
>;
export type VehicleFulfilledPartsResponseModel = z.infer<
  typeof VehicleFulfilledPartsResponseSchema
>;

// Customer Models
export type CustomerVehicleListModel = z.infer<
  typeof CustomerVehicleListSchema
>;

// Fleet Models
export type FleetVehiclesModel = z.infer<typeof FleetVehiclesSchema>;
export type FleetDeleteVehicleRequestModel = z.infer<
  typeof FleetDeleteVehicleRequestSchema
>;

export type FleetVehicleValidateAutoIntegrateRequestModel = z.infer<
  typeof FleetVehicleValidateAutoIntegrateRequestSchema
>;

export type FleetVehicleValidateAutoIntegrateResponseModel = z.infer<
  typeof FleetVehicleValidateAutoIntegrateResponseSchema
>;

// CRUD
export type FleetVehicleCreate = z.infer<typeof FleetVehicleCreateSchema>;

export type FleetVehicleCreateResponseModel = z.infer<
  typeof FleetVehicleCreateResponseSchema
>;

export type DeleteVehicleRequestModel = z.infer<
  typeof DeleteVehicleRequestSchema
>;
export type UpdateFleetVehicleRequestModel = z.infer<
  typeof UpdateFleetVehicleRequestSchema
>;

export type VehicleMileageFormModel = z.infer<typeof VehicleMileageFormSchema>;
export type AddFulfilmentToRequestModel = z.infer<
  typeof AddFulfilmentToRequestSchema
>;
export type FulfilmentToOrderIdModel = z.infer<
  typeof FulfilmentToOrderIdSchema
>;
export type GetFulfilmentToResponseModel = z.infer<
  typeof GetFulfilmentToResponseSchema
>;
export type UpdateFulfilmentToRequestModel = z.infer<
  typeof UpdateFulfilmentToRequestSchema
>;
export type UpdateFulfilmentToResponseModel = z.infer<
  typeof UpdateFulfilmentToResponseSchema
>;
export type UpdateFulfilmentToGeneralAttributeRequestModel = z.infer<
  typeof UpdateFulfilmentToGeneralAttributeRequestSchema
>;
export type FulfilmentToModel = z.infer<typeof FulfilmentToSchema>;
export type SearchFulfilmentToResponseModel = z.infer<
  typeof SearchFulfilmentToResponseSchema
>;

export type GetTechnicianVehicleHistoryModel = z.infer<
  typeof GetTechnicianVehicleHistorySchema
>;
export type GetTechnicianVehicleHistoryResponseModel = z.infer<
  typeof GetTechnicianVehicleHistoryResponseSchema
>;
export type TechnicianVehicleHistoryPartsModel = z.infer<
  typeof TechnicianVehicleHistoryPartsSchema
>;
export type TechnicianVehicleHistoryModel = z.infer<
  typeof TechnicianVehicleHistorySchema
>;
